import { gql } from '@graphql/__generated__';
import type { GetListingDetailsQuery } from '@graphql/__generated__/graphql';

export type GetListingDetailsData = NonNullable<
  GetListingDetailsQuery['lots_by_pk']
>;

export const GET_LOT = gql(/* GraphQL */ `
  query GetLot($id: uuid!) {
    lots_by_pk(id: $id) {
      id
      title
      broker_id
      broker {
        id
        active_teams_users: teams_users(
          where: { deactivated: { _eq: false } }
        ) {
          team_id
          team {
            id
            lots_member_access
          }
        }
      }
      property {
        id

        # to diplay address as fallback when title is empty
        street_number
        route
        locality
        postcode
      }
    }
  }
`);

export const GET_LOTS_LIST = gql(/* GraphQL */ `
  query GetLotsList(
    $where: lots_bool_exp!
    $order_by: [lots_order_by!]!
    $limit: Int!
    $offset: Int!
  ) {
    me {
      id
      teams_users {
        team_id
      }
    }
    lots(where: $where, order_by: $order_by, limit: $limit, offset: $offset) {
      ...LotCardCarousel_lot
      ...LotCardInfo_lot
      ...ListingsDetailedView
      created_at
      updated_at
      signed_at
      title
      offer_type
      sale_price
      rent_net
      rent_extra
      status
      order_nr
      lots_status {
        label
      }
      is_exclusive
      ...ListingCell_listing
      broker {
        id
        active_teams_users: teams_users(
          where: { deactivated: { _eq: false } }
        ) {
          team_id
          team {
            id
            lots_member_access
          }
        }
        ...UserInfo_user
      }
      sellers {
        lot_id
        user_id
        seller {
          id
          # Shadow-banned FSBO advertiser (#24256): the list tags the lot so
          # reviewers can tell a held listing from a real pending review.
          fsbo_blocked_at
          ...UserInfo_user
        }
      }
      # The block is enforced on the creator too (lots trigger, reviewer
      # gate), and an FSBO lot can carry no seller row at all.
      created_by_user {
        id
        fsbo_blocked_at
      }
      portal_listings {
        id
        portal_id
        live
      }
      property {
        id
        route
        street_number
        locality
        postcode
        land_surface
        living_surface
        built_surface
        property_type {
          id
          label
          main_type
        }
        latest_appraisal {
          id
          min
          value
          max
        }
      }
      enquiries {
        id
        portal_id
      }
      enquiries_aggregate {
        aggregate {
          count
          max {
            created_at
          }
        }
      }
    }
    portals(where: { active: { _eq: true } }) {
      id
      team_id
      name
      quota
      type
      portal_listings_aggregate(where: { live: { _eq: true } }) {
        aggregate {
          count
        }
      }
      team {
        id
        name
      }
      teams_portals {
        team_id
      }
    }
  }
`);

export const GET_LOTS_LIST_COUNT = gql(/* GraphQL */ `
  query GetLotsListCount($where: lots_bool_exp!) {
    lots_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const TOGGLE_PORTAL_LISTING = gql(/* GraphQL */ `
  mutation TogglePortalListing(
    $portal_id: uuid!
    $lot_id: uuid!
    $skip_ftp_check: Boolean = false
  ) {
    toggle_portal_listing(
      portal_id: $portal_id
      lot_id: $lot_id
      skip_ftp_check: $skip_ftp_check
    ) {
      id
      portal_id
      lot_id
      lot {
        id
        status
        tenant_id
      }
      portal {
        id
        name
        active
      }
      live
      milliseconds_online
      last_publication_warnings
    }
  }
`);

export const SYNC_PORTAL_LISTING = gql(/* GraphQL */ `
  mutation SyncPortalListing($portalId: uuid!, $lotId: uuid!) {
    update_portal_listing(portal_id: $portalId, lot_id: $lotId) {
      success
      warnings
    }
  }
`);

export const GET_AVAILABLE_PORTALS = gql(/* GraphQL */ `
  query GetAvailableListingPortals($team_ids: [uuid!]!, $lot_id: uuid!) {
    portals(
      order_by: { name: asc }
      where: {
        _or: [
          { portal_listings: { lot_id: { _eq: $lot_id }, live: { _eq: true } } }
          { team_id: { _in: $team_ids }, active: { _eq: true } }
          {
            teams_portals: { team_id: { _in: $team_ids } }
            active: { _eq: true }
          }
        ]
      }
    ) {
      id
      name
      type
      active
      team_id
      teams_portals {
        team_id
      }
    }
  }
`);

export const DELETE_LOT = gql(/* GraphQL */ `
  mutation DeleteLot($id: uuid!) {
    delete_lots_by_pk(id: $id) {
      id
    }
  }
`);

export const LOT_FEED = gql(/* GraphQL */ `
  query LotFeed($lot_id: uuid!) {
    activities: get_activities_feed_for_lot(args: { p_lot_id: $lot_id }) {
      ...ActivityItem
      ...ActivityFiltersActivity
    }
  }
`);

export const GET_LISTING_DETAILS = gql(/* GraphQL */ `
  query GetListingDetails($id: uuid!) {
    lots_by_pk(id: $id) {
      id
      ...ListingOverview
    }
  }
`);

export const GET_LISTING_EDIT_CONTEXT = gql(/* GraphQL */ `
  query GetListingEditContext {
    property_types {
      id
      name
      label
      main_type
      type
    }
    heating_enum {
      value
    }
    heating_distribution_enum {
      value
    }
    cooling_enum {
      value
    }
    bed_type_options: get_translated_enum(
      args: { enum_table_name: "bed_type_enum" }
    ) {
      value
      label
    }
    window_view_options: get_translated_enum(
      args: { enum_table_name: "window_view_enum" }
    ) {
      value
      label
    }
    tenant_gender_options: get_translated_enum(
      args: { enum_table_name: "tenant_gender_enum" }
    ) {
      value
      label
    }
  }
`);

export const FILE_FRAGMENT = gql(/* GraphQL */ `
  fragment File on files {
    id
    name
    url
    mimetype
    created_at
    uploader {
      id
      first_name
      last_name
    }
    document_type {
      id
      name
      label
    }
  }
`);

export const UPDATE_LISTING = gql(/* GraphQL */ `
  mutation updateListing($id: uuid!, $lot: lots_set_input) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $lot) {
      id
      ...ListingOverview
    }
  }
`);

// Updates a lot AND inserts `change` activities in one Hasura multi-mutation
// = one Postgres transaction. Callers compute `activities` via
// buildLotChangeActivities (see apollo/utils/lotChangeRules.ts) so the rules
// for which fields trigger activities live in one place.
//
// Mirror of:
//   - agg-next/src/actions/publish.ts (UPDATE_LOT_TRACKED)
//   - realadvisor-app/src/graphql/mutations/publish.ts (UPDATE_LISTING_TRACKED)
//
// All three repos issue the same multi-mutation; keep them in sync whenever
// one of them changes.
export const UPDATE_LISTING_TRACKED = gql(/* GraphQL */ `
  mutation UpdateListingTracked(
    $id: uuid!
    $set: lots_set_input!
    $activities: [activities_insert_input!]!
    $hasActivities: Boolean!
  ) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      ...ListingOverview
    }
    insert_activities(objects: $activities) @include(if: $hasActivities) {
      returning {
        id
      }
    }
  }
`);

export const CREATE_LISTING = gql(/* GraphQL */ `
  mutation CreateListing($lot: InsertListingLotsInsertInput!) {
    insert_lots_derived(lot: $lot) {
      id
      lot {
        id
        ...ListingOverview
      }
    }
  }
`);

export const CREATE_PROPERTY_FILE = gql(/* GraphQL */ `
  mutation CreatePropertyFile($object: property_files_insert_input!) {
    insert_property_files_one(object: $object) {
      order_nr
      is_visible
      property_id
      file_id
      file {
        id
        name
        url
        mimetype
        created_at
        document_type {
          id
          name
          label
        }
      }
    }
  }
`);

// `activities`/`hasActivities` default to empty/false so existing callers that
// don't track changes don't need to pass them. The price form passes
// activities computed via buildLotChangeActivities.
export const UPDATE_LISTING_WITH_PROPERTY = gql(/* GraphQL */ `
  mutation updateListingWithProperty(
    $id: uuid!
    $prop_id: uuid!
    $lot: lots_set_input!
    $property: properties_set_input!
    $activities: [activities_insert_input!]! = []
    $hasActivities: Boolean! = false
  ) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $lot) {
      id
      ...ListingOverview
    }
    update_properties_by_pk(pk_columns: { id: $prop_id }, _set: $property) {
      id
      ...ListingOverviewProperty
    }
    insert_activities(objects: $activities) @include(if: $hasActivities) {
      returning {
        id
      }
    }
  }
`);

export const LISTING_DOCUMENTS = gql(/* GraphQL */ `
  query ListingDocuments($lotId: uuid!) {
    lots_by_pk(id: $lotId) {
      id
      property_id
      property {
        id
        property_files(order_by: { order_nr: asc }) {
          ...PropertyFile
        }
      }
    }
  }
`);

export const UPDATE_FILE = gql(/* GraphQL */ `
  mutation UpdateFile($input: files_set_input!, $id: uuid!) {
    update_files(_set: $input, where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`);

export const UPDATE_PROPERTY_FILE = gql(/* GraphQL */ `
  mutation UpdatePropertyFile(
    $input: property_files_set_input!
    $file_id: uuid!
    $property_id: uuid!
  ) {
    update_property_files(
      _set: $input
      where: { file_id: { _eq: $file_id }, property_id: { _eq: $property_id } }
    ) {
      returning {
        property_id
        file_id
        is_visible
      }
    }
  }
`);

export const REORDER_PROPERTY_FILES = gql(/* GraphQL */ `
  mutation ReOrderPropertyFiles(
    $property_id: uuid!
    $file_id: uuid!
    $target_position: Int!
  ) {
    update_property_files(
      where: { property_id: { _eq: $property_id }, file_id: { _eq: $file_id } }
      _set: { order_nr: $target_position }
    ) {
      returning {
        property_id
        file_id
        order_nr
      }
      affected_rows
    }
  }
`);

export const INSERT_LOTS = gql(/* GraphQL */ `
  mutation InsertLots($objects: [LotImportInput!]!, $development_id: uuid) {
    import_lots(objects: $objects, development_id: $development_id) {
      lot_id
    }
  }
`);

export const DELETE_LOTS = gql(/* GraphQL */ `
  mutation DeleteLots($ids: [uuid!]!) {
    delete_lots(where: { id: { _in: $ids } }) {
      affected_rows
    }
  }
`);

export const EXPORT_LOTS = gql(/* GraphQL */ `
  query ExportLots($where: lots_bool_exp!) {
    lots(where: $where) {
      id
      lot_reference
      status
      title
      description
      offer_type
      price_unit
      currency
      sale_price
      rent_extra
      rent_net
      monthly_rent_computed
      available_from
      brochure_title_en
      brochure_title_fr
      brochure_title_de
      brochure_title_it
      brochure_title_pl
      brochure_title_pt
      brochure_title_es
      brochure_description_de
      brochure_description_en
      brochure_description_es
      brochure_description_fr
      brochure_description_it
      brochure_description_pl
      brochure_description_pt
      video_url
      virtual_visit_url

      # Settings
      use_custom_brochure_description
      use_custom_brochure_title
      hide_price_on_brochure
      hide_price_on_portals
      send_enquiries_to_sellers
      origin_id
      shared_on_mls_at
      mls_share_mode
      mls_terms_enabled
      mls_commission_type
      mls_buyer_agent_share_percent
      mls_buyer_agent_share_fixed_amount
      enquiry_response_enabled

      # Mandate
      mandate_type
      is_exclusive
      signed_at
      is_sales_tax_inclusive
      commission_rate
      duration_in_months
      fixed_fee
      has_success_fee
      order_nr
      mandate_sale_price
      minimum_net_seller_price
      success_fee_percentage
      expected_time_to_sell
      success_fee_custom
      success_fee_type

      broker {
        full_name
        emails(limit: 1) {
          email
        }
      }
      development_id

      created_at
      updated_at

      property {
        id
        ## Property type
        property_type {
          id
          name
          main_type
        }
        ## Address fields
        route
        street_number
        locality
        postcode
        state
        country
        country_code
        lat
        lng
        macro_location
        micro_location

        ## Surfaces
        living_surface
        usable_surface
        weighted_floor_surface
        gross_floor_surface
        built_surface
        land_surface
        balcony_surface
        terrace_surface
        garden_surface
        basement_surface
        commercial_surface
        residential_surface
        ceiling_height
        building_volume
        building_volume_norm

        ## Rooms & floors
        number_of_rooms
        number_of_bedrooms
        number_of_bathrooms
        number_of_toilets
        number_of_floors
        floor_of_flat

        ## Parking
        number_of_indoor_parkings
        number_of_outdoor_parkings
        parking_potential_rental_income
        parking_yearly_rental_income
        income_includes_parking

        ## Features
        number_of_lifts
        minergie_code
        is_oriented_north
        is_oriented_south
        is_oriented_east
        is_oriented_west
        has_view
        has_fireplace
        has_sauna
        has_gym
        has_wellness_area
        has_photovoltaic_panels
        has_swimming_pool
        has_attic
        has_cellar
        has_storage_room
        has_elevator
        is_child_friendly
        is_commercial_fully_rented
        is_luxury
        is_maisonette
        is_parking_fully_rented
        is_rental
        is_residential_fully_rented
        is_secondary_residence
        is_wheelchair_accessible

        ## building
        construction_year
        renovation_year
        building_condition
        flat_condition
        plot_number
        plot_registry_number
        plot_is_constructible
        plot_has_constructible_reserve
        number_of_residential_units
        number_of_commercial_units

        ## Financials
        share_of_ownership
        residential_yearly_rental_income
        residential_potential_rental_income
        commercial_yearly_rental_income
        commercial_potential_rental_income
        renovation_fund

        ## Energy
        minergie_code
        heating
        cooling
        heating_distribution
        energy_certificate_version
        energy_consumption_class
        energy_reference_year
        energy_inspection_date
        emissions
        gas_emissions_class
        estimated_higher_energy_cost
        estimated_lower_energy_cost

        created_at
        updated_at

        property_images {
          id
          image {
            id
            url
          }
        }

        property_files {
          property_id
          file_id
          file {
            id
            url
          }
        }
      }
    }
  }
`);

export const REORDER_LOTS = gql(/* GraphQL */ `
  mutation UpdateLotsOrder($id: uuid!, $target_position: Int!) {
    update_lots_by_pk(
      pk_columns: { id: $id }
      _set: { order_nr: $target_position }
    ) {
      id
    }
  }
`);

export const UPDATE_AUTO_MATCHING_FILTERS = gql(/* GraphQL */ `
  mutation UpdateAutoMatchingFilters($id: uuid!, $filters: jsonb) {
    update_lots_by_pk(
      pk_columns: { id: $id }
      _set: { auto_matching_filters: $filters }
    ) {
      id
      auto_matching_filters
    }
  }
`);

export const GET_LISTING_SURROUNDING_PLACES = gql(/* GraphQL */ `
  query GetListingSurroundingPlaces($id: uuid!) {
    get_surrounding_listing_places(listingId: $id, placeType: "locality") {
      place_id
    }
  }
`);

export const GET_DEVELOPMENT = gql(/* GraphQL */ `
  query GetDevelopment($id: uuid!) {
    developments_by_pk(id: $id) {
      id
      title
      status
      is_published
      development_images(order_by: { order_nr: asc }, limit: 1) {
        image {
          id
          url
        }
      }
    }
  }
`);

// Account-level block of an FSBO advertiser (#24256). The action flags the
// account and takes its live listings down; the reviewer then holds every
// later submission from it.
// Shadow ban of FSBO advertisers (#24256): one call for the advertiser and
// the linked accounts the reviewer ticked. `batch_id` joins an existing
// block batch (the linked accounts are blocked already) instead of opening
// a new one, so "unblock all" undoes the whole group.
export const BLOCK_FSBO_USERS = gql(/* GraphQL */ `
  mutation BlockFsboUsers(
    $user_ids: [uuid!]!
    $lot_id: uuid
    $reason: String
    $batch_id: uuid
  ) {
    block_fsbo_users(
      user_ids: $user_ids
      lot_id: $lot_id
      reason: $reason
      batch_id: $batch_id
    ) {
      batch_id
      blocked_user_ids
      unpublished_lots
      failed_portal_listings
      failed_lots
    }
  }
`);

export const UNBLOCK_FSBO_USERS = gql(/* GraphQL */ `
  mutation UnblockFsboUsers($user_ids: [uuid!]!) {
    unblock_fsbo_users(user_ids: $user_ids) {
      user_ids
    }
  }
`);

// The other accounts blocked in the same batch as an advertiser, so the
// Unblock dialog can offer to lift the whole batch. A batch grows without
// bound (every automatic same-browser block joins it), so the list is not
// capped at the action's per-call limit: the dialog unblocks in chunks.
export const GET_FSBO_BLOCK_BATCH = gql(/* GraphQL */ `
  query GetFsboBlockBatch($batch_id: uuid!, $user_id: uuid!) {
    users(
      where: {
        fsbo_block_batch_id: { _eq: $batch_id }
        id: { _neq: $user_id }
        fsbo_blocked_at: { _is_null: false }
      }
      order_by: { fsbo_blocked_at: asc }
      limit: 1000
    ) {
      id
      full_name
      fsbo_blocked_at
    }
  }
`);

// "N other accounts share this name / address / device / network" for a
// listing, from the fsbo_seller_cluster SQL function (90-day window).
export const GET_FSBO_SELLER_CLUSTER = gql(/* GraphQL */ `
  query GetFsboSellerCluster($lot_id: uuid!) {
    fsbo_seller_cluster(args: { p_lot_id: $lot_id }) {
      lot_id
      kind
      value
      other_accounts
      rejected_accounts
      blocked_accounts
      accounts
      network_owner
    }
  }
`);
